import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import DialogueCard from "../components/DialogueCard";
import { TextField } from "../../../components/fields/base/TextField";
import { Button } from "../../../components/core/basic/Button";
import { useCreateShopifyConnection } from "./useCreateShopifyConnection";
import { useShopifyConnectionStatus } from "./useShopifyConnectionStatus";
import { useSetOrgId } from "../../admin/api/useSetCurrentOrg";
import { useAppDispatch } from "../../../redux/hooks";
import { setChannel } from "../../../redux/reducers/channel";
import { CHANNEL_STORAGE_KEY, SECONDARY_CHANNEL_STORAGE_KEY } from "../shared/constants";
import { css } from "../../../../styled-system/css";
import { Box, Center, VStack } from "../../../../styled-system/jsx";

export const ShopifyApiConnection = () => {
  const initialStore = useSearch({ strict: false, select: (s) => s.store }) ?? "";
  const [store, setStore] = useState(initialStore);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [selectedCard, setSelectedCard] = useState<number | null>(null);

  const navigate = useNavigate();
  const createConnection = useCreateShopifyConnection();
  const { data: connectionStatus } = useShopifyConnectionStatus();
  const setOrgId = useSetOrgId();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["onboarding"]);

  const handleSubmit = () => {
    createConnection.mutate(
      { store, client_id: clientId, client_secret: clientSecret },
      {
        onSuccess: (data) => {
          const isSecondaryChannel = localStorage.getItem(SECONDARY_CHANNEL_STORAGE_KEY) === "shopify";

          if (isSecondaryChannel) {
            dispatch(setChannel("shopify"));
            localStorage.setItem(CHANNEL_STORAGE_KEY, "shopify");
            localStorage.removeItem(SECONDARY_CHANNEL_STORAGE_KEY);
            setOrgId.mutate(data.org_id);
          } else {
            void navigate({ to: "/user/shopifysummary" });
          }
        },
      },
    );
  };

  const isConnected = connectionStatus?.is_connected ?? false;
  const canSubmit = store.trim() !== "" && clientId.trim() !== "" && clientSecret.trim() !== "";

  return (
    <Box marginY="xlarge" maxWidth="800px" marginX="auto">
      <VStack gap="small" alignItems="stretch">
        <h2 className={css({ margin: 0, fontWeight: 600 })}>{t(($) => $.onboarding["Connect Shopify"])}</h2>
        <p className={css({ fontSize: "medium" })}>
          {t(($) => $.onboarding["Enter your Shopify Client Credentials from the Dev Dashboard"])}
        </p>
      </VStack>

      <VStack gap="medium" marginTop="large">
        <DialogueCard
          title={t(($) => $.onboarding["Shopify Store Credentials"])}
          description=""
          justify="center"
          onPress={() => setSelectedCard(selectedCard === 0 ? null : 0)}
          successIcon={isConnected}
        >
          {selectedCard === 0 && (
            <form
              onSubmit={(e) => e.preventDefault()}
              className={css({ display: "flex", gap: "large", flexDirection: "column", width: "100%" })}
            >
              <TextField
                type="text"
                name="store"
                value={store}
                label={t(($) => $.onboarding["Store URL"])}
                description={t(($) => $.onboarding["e.g. my-shop or my-shop.myshopify.com"])}
                onChange={(value) => setStore(value)}
              />
              <TextField
                type="text"
                name="clientId"
                value={clientId}
                label={t(($) => $.onboarding["Client ID"])}
                onChange={(value) => setClientId(value)}
              />
              <TextField
                type="password"
                name="clientSecret"
                value={clientSecret}
                label={t(($) => $.onboarding["Client Secret"])}
                onChange={(value) => setClientSecret(value)}
              />
              <Center>
                <Button
                  onPress={handleSubmit}
                  variant="primary"
                  isDisabled={!canSubmit}
                  isPending={createConnection.isPending}
                  width="50%"
                  colorScheme="fuchsia"
                >
                  {t(($) => $.onboarding.Connect)}
                </Button>
              </Center>
            </form>
          )}
        </DialogueCard>
      </VStack>
    </Box>
  );
};
